import { AxiosRequestConfig } from "axios";

import {
  BulkUpdateParams,
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import useAxios, { IApiResponse } from "src/core/shared/hooks/useAxios";
import {
  IDeliveryRoute,
  IDrop
} from "src/core/drops/api/drops.api";
import { IList } from "src/core/lists/api/lists.api";
import {
  ErrorType,
  IError,
  IOrderLineError
} from "src/core/errors/api/errors.api";
import { IProduct } from "src/core/products/api/products.api";
import { ITransaction } from "src/modules/payment/api/transactions.api";
import { IProductSubstitution } from "src/modules/substitutions/api/substitutions.api";
import { IUser } from "src/core/users/api/users.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import { IProcessJob } from "../../process-job/api/processJobs.api";

type OrderApi =
  | "LIST_ORDERS"
  | "LIST_DROP_ORDERS"
  | "CREATE_ORDER"
  | "DUPLICATE_ORDER"
  | "SUBMIT_ORDER"
  | "CANCEL_ORDER"
  | "UPDATE_ORDER"
  | "BULK_UPDATE_ORDERS"
  | "PRE_BULK_REPLACE_PRODUCT"
  | "GET_ORDER"
  | "LIST_ERROR_ORDERS"
  | "COUNT_ORDERS"
  | "RESYNC_ORDER"
  | "LIST_PROCESS_JOB_ORDERS"
  | "LIST_PROCESS_JOB_ORDERS_BY_AUDIT_LOG"
  | "LIST_ORDER_AUDIT_LOGS"
  | "DELETE_ORDER"
  | "INVOICE_ORDER";

export type ListOrdersParams = { pagination: PaginatedParams<IOrder>; filters: FilterParams<IOrder> };
export type ExportOrdersParams = { sinceLast: boolean; filters: FilterParams<IOrder> };
export type CountOrdersParams = { filters: FilterParams<IOrder> };
export type ListOrdersByErrorParams = {
  pagination: PaginatedParams<IOrder>;
  filters: FilterParams<IOrder> & { errors: FilterParams<IError> };
};
export type ListOrdersByDropParams = { dropId: number | null; pagination: PaginatedParams<IOrder>; filters: FilterParams<IOrder> };
export type ListOrdersByProcessJobFilterParams = FilterParams<IOrder> & { auditLogType?: string; };
export type ListOrdersByProcessJobParams = { processJobId: number | null; pagination: PaginatedParams<IOrder>; filters: ListOrdersByProcessJobFilterParams};
export type ListOrderLogsParams = { orderId: number | null; pagination: PaginatedParams<IOrder>; filters: FilterParams<IOrder> };

export type CreateOrderParams = Pick<IOrder, "branchId" | "orderStatusId" | "customerReference" | "orderDeliveryDate" | "orderLines" | "type"> & Partial<Pick<IOrder, "origin">>;
export type SubmitOrderParams = Omit<IOrder, "orderLines"> & { orderLines: Array<Omit<IOrderLine, "product">> };
export type ResyncOrderParams = { orderNumber: string; deliveryDate?: Date };
export type DuplicateOrderParams = { deliveryDate: Date, orderId: number; editBeforePlacing: boolean; customerReference?: string };
export type UpdateOrderParams = Partial<IOrder> & Pick<IOrder, "id">;
export type UpdateOrderStatusParams = { orderId: number; orderStatusId: number };
export type CancelOrderParams = number;
export type InvoiceOrderParams = Pick<IOrder, "invoiceNumber" | "id">;
export enum BulkOrderActionsType {
  NOTE = "note",
  CANCEL = "cancel",
  RESYNC = "resync",
  REPLACE_PRODUCT = "replace_product"
}
export type BulkUpdateOrdersParams = BulkUpdateParams<{
  fromProductId?: number;
  toProductId?: number;
  note?: Pick<IOrderNote, "authorId" | "body" | "source">;
}> & { type: BulkOrderActionsType };
export type PreBulkProductReplaceParams = {
  fromProductId: number;
  toProductId: number;
  orderIds: number[];
};
export type CountOrdersResult = { count: number };

export type OrdersResponse = {
  orders: PaginatedData<IOrder>;
  allOrdersWithErrors: number;
};
export const ordersApi = <R, P = any>(name: OrderApi): [IApiResponse<R, any>, <C = P>(P: C, config?: Pick<AxiosRequestConfig, "cache">) => void] => {
  switch (name) {
    case "LIST_DROP_ORDERS":
      return useAxios<R>((params: ListOrdersByDropParams) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/core/orders/branch/${params.dropId}`,
          method: "GET",
          params
        };
      }, name);

    case "LIST_ORDERS":
      return useAxios<R>(
        (params: ListOrdersParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders`,
          method: "GET",
          params
        }),
        name
      );

    case "LIST_ERROR_ORDERS":
      return useAxios<R>(
        (params: ListOrdersByErrorParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/error/all`,
          method: "GET",
          params
        }),
        name
      );

    case "COUNT_ORDERS":
      return useAxios<R>(
        (params: CountOrdersParams, config) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/count/all`,
          method: "GET",
          params,
          cache: config?.cache ? config.cache : {
            clearOnError: true,
            maxAge: cacheMinutesToMilliseconds(60),
            key: () => {
              return `${process.env.REACT_APP_API_URL}/core/orders/count/all?${JSON.stringify(params.filters)}`;
            }
          }
        }),
        name
      );

    case "GET_ORDER":
      return useAxios<R>(
        orderId => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/${orderId}`,
          method: "GET"
        }),
        name
      );

    case "CREATE_ORDER":
      return useAxios<R>(
        (data: CreateOrderParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/`,
          method: "POST",
          data
        }),
        name
      );

    case "DUPLICATE_ORDER":
      return useAxios<R>(
        ({
          deliveryDate, orderId, customerReference, editBeforePlacing
        }: DuplicateOrderParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/${orderId}/duplicate`,
          method: "POST",
          data: {
            deliveryDate,
            customerReference,
            editBeforePlacing
          }
        }),
        name
      );

    case "UPDATE_ORDER":
      return useAxios<R>(
        (data: UpdateOrderParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/${data.id}`,
          method: "PATCH",
          data
        }),
        name
      );

    case "BULK_UPDATE_ORDERS":
      return useAxios<R>(
        (data: BulkUpdateOrdersParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders`,
          method: "PATCH",
          data
        }),
        name
      );

    case "PRE_BULK_REPLACE_PRODUCT":
      return useAxios<R>(
        (data: PreBulkProductReplaceParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/pre-update/bulk-replace-product`,
          method: "POST",
          data
        }),
        name
      );

    case "SUBMIT_ORDER":
      return useAxios<R>(
        (order: SubmitOrderParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/${order.id}/submit`,
          method: "PATCH",
          data: order
        }),
        name
      );

    case "DELETE_ORDER":
      return useAxios<R>(
        (orderId: number) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/${orderId}`,
          method: "DELETE"
        }),
        name
      );

    case "RESYNC_ORDER":
      return useAxios<R>(
        (data: ResyncOrderParams) => ({
          url: `${process.env.REACT_APP_API_URL}/webhook/orders/update`,
          method: "POST",
          headers: {
            // TODO: REMOVE TOKEN FROM HERE
            Authorization: `Bearer ${process.env.REACT_APP_JWT_RESYNC_TOKEN}`
          },
          data
        }),
        name
      );

    case "CANCEL_ORDER":
      return useAxios<R>(
        (orderId: CancelOrderParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/${orderId}/cancel`,
          method: "PATCH"
        }),
        name
      );

    case "LIST_PROCESS_JOB_ORDERS":
      return useAxios<R>(({ processJobId, ...params }: ListOrdersByProcessJobParams) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/core/orders/process-job/${processJobId}`,
          method: "GET",
          params
        };
      }, name);

    case "LIST_PROCESS_JOB_ORDERS_BY_AUDIT_LOG":
      return useAxios<R>(({ processJobId, ...params }: ListOrdersByProcessJobParams) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/core/orders/audit-logs/${processJobId}`,
          method: "GET",
          params
        };
      }, name);

    case "LIST_ORDER_AUDIT_LOGS":
      return useAxios<R>(({ orderId, ...params }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/core/orders/${orderId}/audit-logs`,
          method: "GET",
          params
        };
      }, name);

    case "INVOICE_ORDER": {
      return useAxios<R>(
        (order: InvoiceOrderParams) => ({
          url: `${process.env.REACT_APP_API_URL}/core/orders/${order.id}/invoice`,
          method: "PATCH",
          data: order
        }),
        name
      );
    }

    default:
      throw new Error("ordersApi requires a name parameter");
  }
};

export interface IOrder {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  orderDeliveryDate: Date | null;
  branchId: number;
  orderStatusId: number;
  customerReference?: string;
  orderPlacedDate?: Date;
  invoiceNumber?: string;
  invoiceDate?: Date;
  orderLines: IOrderLine[];
  branch?: IDrop;
  list?: IList;
  orderStatus?: IOrderStatus;
  errors?: IError[];
  orderNotes?: IOrderNote[];
  listId?: number;
  scheduleId?: number;
  deliveryRoute?: IDeliveryRoute;
  orderReference?: string;
  amendmentNumber?: number;
  amendmentExpiryDate?: Date;
  type: OrderType;
  origin: OrderOrigin;
  deliveryCharge?: IOrderDeliveryCharge;
  imports?: Array<IProcessJob<"import-file-order">>;
  paymentStatus?: OrderPaymentStatus | null;
  transactions?: ITransaction[];
  customData?: Record<string, {
    description: string;
    value: string;
    admin?: boolean;
  }>;
  erpExported?: boolean;
  erpSyncStatus?: ERPSyncStatus | null;
  requestedDeliveryTime?: string | null

  // RJL module props
  rjlDeliveryNumber?: RJLDeliveryNumber;
  // rjlExported?: boolean;
  // rjlSyncStatus?: RJLSyncStatus | null;
}

export type IOrderKeys = keyof Partial<IOrder>;

export enum ERPSyncStatus {
  UpToDate = "up_to_date",
  Updating = "updating",
  ImportPending = "import_pending",
  ExportPending = "export_pending",
  ExportFailed = "export_failed",
  ImportFailed = "import_failed"
}

export enum RJLDeliveryNumber {
  "Number1" = 1,
  "Number2" = 2,
  "Number3" = 3,
  "Number4" = 4,
  "Number5" = 5,
  "Number6" = 6,
  "Number7" = 7,
  "Number8" = 8,
  "Number9" = 9
}
/**
 * Method by which the order was created
 */
export enum OrderOrigin {
  File = "file",
  Prediction = "prediction",
  Manual = "manual",
  StandingOrder = "standing_order",
  DuplicateOrder = "duplicate_order",
  List = "list",
  API = "api",
  Reminder = "reminder"
}

export type OrderType = "scheduled" | "bulk" | "adhoc";
export enum OrderPaymentStatus {
  "CAPTURED" = "captured",
  "UNCAPTURED" = "uncaptured",
  "CANCELED" = "canceled",
  "REFUNDED" = "refunded",
  "PARTIAL_REFUNDED" = "partially_refunded",
  "FAILED" = "failed",
  "REFUND_FAILED" = "refund_failed"
}
export type OrderMode = "edit" | "view";

export interface IOrderStatus {
  id: number;
  name: string;
  code: string;
  description: string | null;
  settings: OrderStatusSettings;
}

export interface OrderStatusSettings {
  lockOrders: boolean;
  allowedTransitions: string[];
  group: OrderStatusGroupType;
}

export interface IOrderDeliveryCharge {
  id: number;
  orderId: number;
  price: number | null;
  productId: number;
  product?: IProduct;
}

export type OrderStatusGroupType = "DRAFT" | "ERROR" | "PROVISIONAL" | "DUE" | "CANCELLED" | "UPDATING" | "SMART";

export interface IOrderLine {
  id?: number;
  orderId?: number;
  price: number | null;
  totalPrice?: number | null;
  productId: number;
  quantity: number;
  product: IProduct;
  errors?: IOrderLineError[];
  totalVatValue: number | null;
  vatPercentage: number | null;
  substituteOrderLineId?: number | null;
  substituteOrderLine?: IOrderLine;
  productSubstitutionId?: number | null;
  productSubstitution?: IProductSubstitution;
  replacement?: boolean;
  externalId?: string | null;
}

export interface ICurrentOrder {
  data: IOrder | null;
  mode: OrderMode;
  saved: boolean;
  allowedDays: number | null;
}

export interface IErrorCode {
  id: number;
  note: string;
  description: string;
  code: string;
  type: ErrorType;
}

export enum OrderNoteSourceEnum {
  SYSTEM = "System",
  STAFF = "Staff",
  CUSTOMER = "Customer"
}

export interface IOrderNote {
  id?: number;
  orderId: number;
  body: string;
  authorId: number;
  author?: IUser
  source: OrderNoteSourceEnum;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IOrderAuditLog<T extends OrderAuditLogChangeType = OrderAuditLogChangeType> {
  id?: number;
  orderId: number;
  processJobId?: number;
  processJob: IProcessJob;
  updatedByUserId: number;
  updatedByUser: IUser;
  changedBy: OrderAuditLogChange;
  type: T;
  createdAt: Date;
  fullData: AuditLogChangeData[T];
}

export type ListAuditLogParams = { orderId: number; pagination: PaginatedParams<IOrderAuditLog>; };

export enum OrderAuditLogChange {
  User = "user",
  Staff = "staff",
  System = "system"
}

export enum OrderAuditLogChangeType {
  OrderCreated = "order_created",
  PriceChange = "price_change",
  AddProduct = "add_product",
  RemoveProduct = "remove_product",
  QuantityChange = "quantity_change",
  ProductSubstitution = "product_substitution",
  StatusChange = "status_change",
  ProductReplacement = "product_replacement",
  OrderTypeChange = "order_type_change",
  OrderExported = "order_exported",
  OrderImported = "order_imported",
  Message = "message"
}

export interface AuditLogChangeData {
  [OrderAuditLogChangeType.OrderCreated]: {
    deliveryDate: string | Date | null;
    type: OrderType;
    fromOrderId?: number;
  };
  [OrderAuditLogChangeType.PriceChange]: {
    fromPrice: number;
    toPrice: number;
    productId: number;
    product: IProduct;
  };
  // replacements are permenant unreversible actions
  [OrderAuditLogChangeType.ProductReplacement]: {
    quantity?: number;
    fromProductId: number;
    toProductId: number;
    fromProduct: IProduct;
    toProduct: IProduct;
  };
  // subs are reversible and trackable
  [OrderAuditLogChangeType.ProductSubstitution]: {
    quantity: number;
    fromProductId: number;
    toProductId?: number;
    fromProduct: IProduct;
    toProduct?: IProduct;
    productSubstitutionId: number;
  };
  [OrderAuditLogChangeType.AddProduct]: {
    quantity: number;
    productId: number;
    product: IProduct;
  };
  [OrderAuditLogChangeType.RemoveProduct]: {
    productId: number;
    product: IProduct;
    reason?: string;
  };
  [OrderAuditLogChangeType.QuantityChange]: {
    fromQuantity: number;
    toQuantity: number;
    productId: number;
    product: IProduct;
  };
  [OrderAuditLogChangeType.StatusChange]: {
    toOrderStatusId: number;
    fromOrderStatusId: number;
    fromOrderStatus: IOrderStatus;
    toOrderStatus: IOrderStatus;
  };
  [OrderAuditLogChangeType.OrderTypeChange]: {
    fromOrderType: OrderType;
    toOrderType: OrderType;
  };
  [OrderAuditLogChangeType.OrderExported]: {
    exportName: string;
  };
  [OrderAuditLogChangeType.OrderImported]: {
    from: string;
  };
  [OrderAuditLogChangeType.Message]: {
    text: string;
  }
}
